import { FormattedNumberInputField, TextareaInputField } from "@/components/shared";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { UseFormReturn } from "react-hook-form";
import { positiveInteger, postiveNumberZeroToOneHundred } from "@/utils/form";
import { getInputWidth } from "../../Shared/helpers";
import { cn } from "@/lib/utils";

export const createEstimateSchema = (t: TFunction) => {
  return z.object({
    estimate: z.object({
      revenue: positiveInteger(t("newtender.formgroups.estimate.inputs.revenue.label")),
      hoursPerMonth: positiveInteger(t("newtender.formgroups.estimate.inputs.hourspermonth.label")),
      margin: postiveNumberZeroToOneHundred(t("newtender.formgroups.estimate.inputs.margin.label")),
      effort: positiveInteger(t("newtender.formgroups.estimate.inputs.effort.label")),
      description: z.string()
        .max(500, {
          message: t("schemas:createtender.estimate.descriptionmax"),
        }).optional(),
    }),
  });
};

export const estimateDefaultValues = {
  estimate: {
    revenue: undefined,
    hoursPerMonth: undefined,
    margin: undefined,
    effort: undefined,
    description: "",
  }
};

export function EstimateFormGroup({ form, leftPanelWidth }: { form: UseFormReturn<any>, leftPanelWidth: number }) {
  const { t } = useTranslation();

  return (
    <div>
      <div id="estimate" className="form-field-container">
        {/* revenue */}
        <FormattedNumberInputField
          control={form.control}
          name="estimate.revenue"
          label={t("newtender.formgroups.estimate.inputs.revenue.label")}
          placeholder={t("newtender.formgroups.estimate.inputs.revenue.placeholder")}
          isStandalone
          isInteger
          style="currency"
          className={cn(getInputWidth(leftPanelWidth, true))}
        />

        {/* hoursPerMonth */}
        <FormattedNumberInputField
          control={form.control}
          name="estimate.hoursPerMonth"
          label={t("newtender.formgroups.estimate.inputs.hourspermonth.label")}
          placeholder={t("newtender.formgroups.estimate.inputs.hourspermonth.placeholder")}
          additionalSuffix={t("newtender.formgroups.estimate.inputs.hourspermonth.suffix")}
          isStandalone
          isInteger
          className={cn(getInputWidth(leftPanelWidth, true))}
          spaceBetweenSuffix={true}
        />

        {/* Margin */}
        <FormattedNumberInputField
          control={form.control}
          name="estimate.margin"
          label={t("newtender.formgroups.estimate.inputs.margin.label")}
          placeholder={t("newtender.formgroups.estimate.inputs.margin.placeholder")}
          additionalSuffix="%"
          isStandalone
          className={cn(getInputWidth(leftPanelWidth))}
        />

        {/* Effort */}
        <FormattedNumberInputField
          control={form.control}
          name="estimate.effort"
          label={t("newtender.formgroups.estimate.inputs.effort.label")}
          placeholder={t("newtender.formgroups.estimate.inputs.effort.placeholder")}
          additionalSuffix={t("newtender.formgroups.estimate.inputs.effort.suffix")}
          isInteger
          isStandalone
          spaceBetweenSuffix={true}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        {/* Description */}
        <TextareaInputField
          form={form}
          name="estimate.description"
          label={t("newtender.formgroups.estimate.inputs.description.label")}
          placeholder={t("newtender.formgroups.estimate.inputs.description.placeholder")}
          showOptional
          formItemClassName={cn(getInputWidth(leftPanelWidth))}
        />
      </div>
    </div>
  );
}
